/**
 * Admin: the <Noun> settings page (SA-1204 §4, SA-1217). The first-quote target and the quote
 * decision reminder, in minutes, read and written together through `/api/admin/record-settings`.
 */
import { useState, type FormEvent } from 'react'
import { Button, Input, Spinner } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { useRecordSettings, useSaveRecordSettings, type RecordSettings } from '@/hooks/useRecordSettings'
import * as text from './recordSettingsText'

const MIN_MINUTES = 5
const MAX_MINUTES = 1440
/** A year: the server's `REMINDER_MAX` (SA-1217). */
const MAX_REMINDER = 525600

/** A whole number of minutes inside the server's bounds, or null. */
function parseMinutes(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null
  const n = Number(raw)
  return n >= MIN_MINUTES && n <= MAX_MINUTES ? n : null
}

/** A whole number of minutes from 0 to a year, or null. */
function parseReminder(raw: string): number | null {
  const text = raw.trim()
  if (!/^\d+$/.test(text)) return null
  const n = Number(text)
  return n <= MAX_REMINDER ? n : null
}

function Editor({ saved }: { saved: RecordSettings }) {
  const save = useSaveRecordSettings()
  const [typed, setTyped] = useState<string | null>(null)
  const [typedReminder, setTypedReminder] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reminderError, setReminderError] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('')
  const value = typed ?? String(saved.first_quote_target_minutes)
  const reminder = typedReminder ?? String(saved.quote_decision_reminder_minutes)

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setStatus('')
    const minutes = parseMinutes(value)
    const reminderMinutes = parseReminder(reminder)
    setError(minutes === null ? text.BOUNDS_ERROR : null)
    setReminderError(reminderMinutes === null ? text.REMINDER_BOUNDS_ERROR : null)
    if (minutes === null || reminderMinutes === null) return
    try {
      await save.mutateAsync({ first_quote_target_minutes: minutes, quote_decision_reminder_minutes: reminderMinutes })
      setTyped(null)
      setTypedReminder(null)
      setStatus(text.SAVED)
    } catch {
      setStatus(text.SAVE_FAILED)
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Input
        label={text.TARGET_LABEL}
        type="number"
        inputMode="numeric"
        step="any"
        value={value}
        onChange={(e) => { setTyped(e.target.value); setError(null); setStatus('') }}
        error={error ?? undefined}
        hint={text.targetHelp(saved.first_quote_target_minutes)}
      />
      <p className="text-sm text-muted-foreground">{text.TARGET_UNIT}</p>
      <Input
        label={text.REMINDER_LABEL}
        type="number"
        inputMode="numeric"
        step="any"
        value={reminder}
        onChange={(e) => { setTypedReminder(e.target.value); setReminderError(null); setStatus('') }}
        error={reminderError ?? undefined}
        hint={text.REMINDER_HELP}
      />
      <p className="text-sm text-muted-foreground">{text.TARGET_UNIT}</p>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending}>{text.SAVE}</Button>
        <p role="status" className="text-sm text-muted-foreground">{status}</p>
      </div>
    </form>
  )
}

export function RecordSettingsPage() {
  const noun = useRecordNoun()
  const settings = useRecordSettings()
  return (
    <PageFrame width="form">
      <PageHeader title={text.pageTitle(noun.singular)} />
      {settings.data ? <Editor saved={settings.data} /> : settings.isError ? (
        <p role="alert" className="text-sm text-destructive">{text.LOAD_FAILED}</p>
      ) : <Spinner />}
    </PageFrame>
  )
}
